/** 手机触控：独立操作按钮使用至少44px点击区域；桌面保持原紧凑布局，业务权限、提交与跳转逻辑不变。 */
import { ResponsiveTable } from "../mobile/ResponsiveTable";
/** 手机记录列表复用原有数据、勾选与业务操作；普通记录按字段卡片呈现，交互表头和对比矩阵保留横向浏览。 */
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ClipboardCheck, BellRing, Loader2, RefreshCw } from "lucide-react";
import { Button } from "../ui/button";
import { toast } from "../ui/sonner";

const rateCls = (v) => (v == null ? "bg-slate-200" : v >= 80 ? "bg-emerald-500" : v >= 50 ? "bg-amber-500" : "bg-red-500");

/* 经理评分完成率（销售 VP / admin / CEO）：按分公司列出各销售经理待评 / 已评 / 完成率 / 平均星级，支持催办 */
export function RatingCompletion({ api, zh }) {
  const navigate = useNavigate();
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const load = useCallback(async () => {
    try { const { data: d } = await api.get("/telephony/rating-stats", { params: { days } }); setData(d); }
    catch { setData(null); }
  }, [api, days]);
  useEffect(() => { load(); }, [load]);

  const nudge = async (r) => {
    setBusy(r.username);
    try {
      const { data: d } = await api.post("/telephony/rating-stats/nudge", { username: r.username, days });
      toast.success(zh ? `已催办 ${r.name}：${d.pending} 通待评（累计催办 ${d.nudges} 次）` : `Nudged ${r.name}: ${d.pending} pending (${d.nudges} nudges)`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || (zh ? "催办失败" : "Failed")); }
    setBusy("");
  };
  if (!data) return null;
  const t = data.totals;
  return (
    <div className="rounded-xl border border-slate-200 bg-white" data-testid="rating-completion" data-days={days}>
      <div className="flex items-center gap-2 px-4 py-3 flex-wrap">
        <ClipboardCheck className="h-4 w-4 text-amber-600" />
        <h2 className="text-sm font-semibold text-slate-800">{zh ? "经理评分完成率" : "Manager rating completion"}</h2>
        <span className="text-xs text-slate-500" data-testid="rating-completion-totals">
          {zh ? `近 ${data.days} 天有录音通话 ${t.candidates} 通 · 已评 ${t.rated} · 完成率 ${t.completionRate ?? "—"}${t.completionRate != null ? "%" : ""}${t.overdue ? ` · 超 ${data.escalateDays || 3} 天待评 ${t.overdue}（每日 09:05 自动升级 VP）` : ""}` : `${t.rated}/${t.candidates} rated · ${t.completionRate ?? "—"}%${t.overdue ? ` · ${t.overdue} overdue` : ""}`}
        </span>
        <div className="flex-wrap md:flex-nowrap ml-auto flex items-center gap-1">
          {[7, 30, 90].map((n) => (
            <button key={n} data-testid={`rating-completion-days-${n}`} onClick={() => setDays(n)} className={"min-h-11 min-w-11 md:min-h-0 md:min-w-0 " + (`px-2.5 py-1 text-xs rounded-md font-medium ${days === n ? "bg-slate-900 text-white" : "text-slate-500 hover:bg-slate-100"}`)}>{n}{zh ? "天" : "d"}</button>
          ))}
          <button data-testid="rating-completion-refresh" onClick={load} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 p-1.5 text-slate-400 hover:text-slate-700"><RefreshCw className="h-3.5 w-3.5" /></button>
        </div>
      </div>
      <div className="overflow-x-auto border-t border-slate-100">
        <ResponsiveTable className="w-full text-sm min-w-[900px]" data-testid="rating-completion-table">
          <thead className="bg-slate-50 text-slate-500 text-xs">
            <tr>{[zh ? "排名" : "#", zh ? "分公司" : "Branch", zh ? "销售经理" : "Manager", zh ? "待评录音" : "Pending", zh ? "已评" : "Rated", zh ? "完成率" : "Completion", zh ? "平均星级" : "Avg ★", zh ? "本人评分" : "By self", zh ? "最久待评" : "Oldest", zh ? `超 ${data.escalateDays || 3} 天` : "Overdue", zh ? "催办" : "Nudges", ""].map((h, i) => <th key={i} className="px-3 py-2 text-left font-medium">{h}</th>)}</tr>
          </thead>
          <tbody>
            {data.rows.map((r) => (
              <tr key={`${r.region}-${r.username || "none"}`} data-testid={`rating-completion-row-${r.username || r.region}`} data-rate={r.completionRate ?? ""} className="border-t border-slate-100 hover:bg-slate-50/60">
                <td className="px-3 py-2 font-bold text-slate-700">{r.rank ?? <span className="text-slate-300">—</span>}</td>
                <td className="px-3 py-2 text-slate-600">{r.regionLabel}</td>
                <td className="px-3 py-2 font-semibold text-slate-800">{r.name || <span className="text-red-500 font-normal">{zh ? "未设销售经理" : "No manager"}</span>}</td>
                <td className="px-3 py-2"><span className={r.pending ? "text-amber-700 font-semibold" : "text-slate-400"}>{r.pending}</span></td>
                <td className="px-3 py-2">{r.rated}</td>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-2">
                    <div className="h-1.5 w-20 rounded-full bg-slate-100 overflow-hidden"><div className={`h-full ${rateCls(r.completionRate)}`} style={{ width: `${r.completionRate ?? 0}%` }} /></div>
                    <span className="text-xs font-semibold">{r.completionRate == null ? "—" : `${r.completionRate}%`}</span>
                  </div>
                </td>
                <td className="px-3 py-2">{r.avgStars != null ? `${r.avgStars} ★` : "—"}</td>
                <td className="px-3 py-2">{r.username ? r.ratedByMe : "—"}</td>
                <td className="px-3 py-2">{r.oldestPendingDays == null ? "—" : <span className={r.oldestPendingDays >= 3 ? "text-red-600 font-semibold" : ""}>{zh ? `${r.oldestPendingDays} 天` : `${r.oldestPendingDays}d`}</span>}</td>
                <td className="px-3 py-2" data-testid={`rating-completion-overdue-${r.username || r.region}`}>{r.overdue ? <span className="rounded-full bg-red-100 text-red-700 px-2 py-0.5 text-xs font-semibold">{r.overdue}</span> : <span className="text-slate-300">0</span>}</td>
                <td className="px-3 py-2 text-slate-600" data-testid={`rating-completion-nudges-${r.username || r.region}`} title={zh ? `其中升级 ${r.escalations || 0} 次` : `${r.escalations || 0} escalations`}>{r.username ? <>{r.nudges || 0}{r.escalations ? <span className="ml-1 text-[10px] text-red-500">↑{r.escalations}</span> : null}</> : "—"}</td>
                <td className="px-3 py-2 text-right">
                  {r.username && r.pending > 0 && (
                    <Button size="sm" variant="outline" data-testid={`rating-completion-nudge-${r.username}`} disabled={busy === r.username} onClick={() => nudge(r)} className="h-7 text-xs">
                      {busy === r.username ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <BellRing className="h-3 w-3 mr-1" />}{zh ? "催办" : "Nudge"}
                    </Button>
                  )}
                  {!r.username && <button className="text-xs text-slate-400 hover:text-slate-700 underline" onClick={() => navigate("/admin/users")}>{zh ? "去设置" : "Set up"}</button>}
                </td>
              </tr>
            ))}
            {data.rows.length === 0 && <tr><td colSpan={12} className="text-center text-slate-300 py-6" data-testid="rating-completion-empty">{zh ? "暂无有录音的通话" : "No recorded calls"}</td></tr>}
          </tbody>
        </ResponsiveTable>
      </div>
    </div>
  );
}
